import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CustomerPlanManagementPage from '@/pages/customer/plan-management';
import CustomerInvoicesTab from '@/pages/customer/invoices';
import CustomerPaymentMethodTab from '@/pages/customer/payment-method';
const tabKeys = ['subscriptions', 'invoices', 'payment-method'] as const;
type BillingTabKey = typeof tabKeys[number];

function isBillingTabKey(value: string | null): value is BillingTabKey {
  return Boolean(value && tabKeys.includes(value as BillingTabKey));
}

export default function CustomerBillingPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = isBillingTabKey(searchParams.get('tab'))
    ? (searchParams.get('tab') as BillingTabKey)
    : 'subscriptions';

  const tabItems = useMemo(
    () => [
      {
        value: 'subscriptions',
        label: t('customer.billing.tabs.subscriptions'),
      },
      {
        value: 'invoices',
        label: t('customer.billing.tabs.invoices'),
      },
      {
        value: 'payment-method',
        label: t('customer.billing.tabs.paymentMethod'),
      },
    ],
    [t],
  );

  const handleTabChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', value);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold">{t('customer.billing.title')}</h1>
        <p className="text-muted-foreground">{t('customer.billing.subtitle')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
          {tabItems.map((item) => (
            <TabsTrigger key={item.value} value={item.value} className="justify-center">
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="subscriptions" className="mt-6">
          <CustomerPlanManagementPage />
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          <CustomerInvoicesTab />
        </TabsContent>
        <TabsContent value="payment-method" className="mt-6">
          <CustomerPaymentMethodTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
